import { Capacitor } from '@capacitor/core'
import { localDb } from '@/data/local/database'
import type { DeviceId, UserId, WorkspaceId } from '@/domain/types'

const IDENTITY_KEY = 'activeIdentity'
const DEFAULT_MODULES = ['资料分析', '判断推理', '言语理解', '数量关系', '申论', '常识判断', '复盘整理']
const DEFAULT_TAGS = ['方法优化', '知识点', '计算细节', '概念理解']

export interface ActiveIdentity {
  userId: UserId
  workspaceId: WorkspaceId
  deviceId: DeviceId
  authenticated: boolean
}

function newId(prefix: string) {
  return `${prefix}_${crypto.randomUUID()}`
}

function deviceName() {
  const platform = Capacitor.getPlatform()
  return platform === 'android' ? 'Android 设备' : '网页浏览器'
}

let identityInitialization: Promise<ActiveIdentity> | null = null

async function initializeLocalIdentity(): Promise<ActiveIdentity> {
  const stored = await localDb.appMetadata.get(IDENTITY_KEY)
  if (stored?.value && typeof stored.value === 'object') {
    return stored.value as ActiveIdentity
  }

  const now = Date.now()
  const userId = newId('local_user')
  const workspaceId = newId('workspace')
  const deviceId = newId('device')
  const identity: ActiveIdentity = { userId, workspaceId, deviceId, authenticated: false }

  await localDb.transaction(
    'rw',
    [
      localDb.localUsers,
      localDb.localWorkspaces,
      localDb.workspaceMemberships,
      localDb.localDevices,
      localDb.userSettings,
      localDb.inspirationTags,
      localDb.syncState,
      localDb.appMetadata,
    ],
    async () => {
      await localDb.localUsers.add({
        id: userId,
        displayName: '本地用户',
        role: 'local',
        createdAtMs: now,
        disabledAtMs: null,
      })
      await localDb.localWorkspaces.add({
        id: workspaceId,
        name: '我的学习空间',
        ownerUserId: userId,
        kind: 'private',
        createdAtMs: now,
      })
      await localDb.workspaceMemberships.add({
        workspaceId,
        userId,
        role: 'owner',
        joinedAtMs: now,
      })
      await localDb.localDevices.add({
        id: deviceId,
        userId,
        name: deviceName(),
        platform: Capacitor.getPlatform() === 'android' ? 'android' : 'web',
        createdAtMs: now,
        lastSeenAtMs: now,
        revokedAtMs: null,
      })
      await localDb.userSettings.add({
        id: userId,
        workspaceId,
        ownerUserId: userId,
        displayName: '本地用户',
        email: '',
        avatarUrl: null,
        statusSignature: '每一次专注，都让上岸更近一步。',
        examName: '我的考试目标',
        examDate: '',
        mockName: '',
        mockDate: '',
        examType: '',
        studyModules: DEFAULT_MODULES,
        focusTheme: 'standard',
        createdAtMs: now,
        updatedAtMs: now,
        deletedAtMs: null,
        version: 1,
      })
      const tags = DEFAULT_MODULES.flatMap(module => DEFAULT_TAGS.map(name => ({
        id: newId('tag'),
        workspaceId,
        ownerUserId: userId,
        module,
        name,
        createdAtMs: now,
        updatedAtMs: now,
        deletedAtMs: null,
        version: 1,
      })))
      await localDb.inspirationTags.bulkAdd(tags)
      await localDb.syncState.add({
        workspaceId,
        lastSequence: 0,
        lastSuccessAtMs: null,
        lastAttemptAtMs: null,
        status: typeof navigator === 'undefined' || navigator.onLine ? 'idle' : 'offline',
        errorMessage: null,
        serverTimeOffsetMs: 0,
        endpoint: null,
      })
      await localDb.appMetadata.add({ key: IDENTITY_KEY, value: identity, updatedAtMs: now })
    },
  )
  return identity
}

export function ensureLocalIdentity(): Promise<ActiveIdentity> {
  if (!identityInitialization) {
    identityInitialization = initializeLocalIdentity().catch(error => {
      identityInitialization = null
      throw error
    })
  }
  const current = identityInitialization
  return current.finally(() => {
    if (identityInitialization === current) identityInitialization = null
  })
}

export async function getActiveIdentity() {
  return ensureLocalIdentity()
}

export async function setActiveIdentity(identity: ActiveIdentity) {
  await localDb.appMetadata.put({ key: IDENTITY_KEY, value: identity, updatedAtMs: Date.now() })
}
